/**
 * Iter121G — Add / Edit Interview
 * Routes:
 *   /cases/:caseId/interviews/new
 *   /cases/:caseId/interviews/:interviewId/edit
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, MessageSquare, Loader2, RefreshCw, Save } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

export default function InterviewFormPage() {
  const { caseId, interviewId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const isEdit = Boolean(interviewId);

  const [caseInfo, setCaseInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    interview_date: new Date().toISOString().split('T')[0],
    interview_location: '', interviewee_name: '', relationship_to_case: '',
    observations: '', body_language_notes: '', evidence_collected: '', summary: '',
  });
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (!caseId) return;
    fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setCaseInfo).catch(() => {});
  }, [caseId, token]);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/interviews/${interviewId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const i = await res.json();
      setForm({
        interview_date: i.interview_date || new Date().toISOString().split('T')[0],
        interview_location: i.interview_location || '',
        interviewee_name: i.interviewee_name || '',
        relationship_to_case: i.relationship_to_case || '',
        observations: i.observations || '',
        body_language_notes: i.body_language_notes || '',
        evidence_collected: i.evidence_collected || '',
        summary: i.summary || '',
      });
    } catch (e) {
      toast({ title: 'Failed to load interview', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [isEdit, interviewId, token]);

  useEffect(() => { load(); }, [load]);

  const goBack = () => navigate(`/cases/${caseId}?tab=interviews`);

  const submit = async () => {
    if (!form.interviewee_name?.trim()) {
      toast({ title: 'Missing interviewee', description: 'Interviewee name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const url = isEdit ? `${BACKEND_URL}/api/interviews/${interviewId}` : `${BACKEND_URL}/api/cases/${caseId}/interviews`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Save failed (${res.status})`);
      toast({ title: isEdit ? 'Interview updated' : 'Interview recorded' });
      isEdit ? navigate(`/cases/${caseId}/interviews/${interviewId}/view`) : goBack();
    } catch (e) {
      toast({ title: isEdit ? 'Could not update interview' : 'Could not record interview', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300 p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading interview…
      </div>
    );
  }

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="interview-form-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={goBack} variant="outline" className="border-slate-600 !text-slate-200" data-testid="interview-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Case
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-amber-300" /> {isEdit ? 'Edit Interview' : 'Record Interview'}
          </h1>
          {caseInfo && <p className="text-xs text-slate-400 mt-0.5">
            {caseInfo.case_number} · <span className="text-slate-300">{caseInfo.title}</span>
          </p>}
        </div>
      </div>

      <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-5 space-y-4 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300">Interview Date</Label>
            <Input type="date" value={form.interview_date} onChange={e => setF('interview_date', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" />
          </div>
          <div>
            <Label className="text-slate-300">Location</Label>
            <Input value={form.interview_location} onChange={e => setF('interview_location', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" placeholder="Interview location" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300">Interviewee Name *</Label>
            <Input value={form.interviewee_name} onChange={e => setF('interviewee_name', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" data-testid="interviewee-name-input" />
          </div>
          <div>
            <Label className="text-slate-300">Relationship to Case</Label>
            <Input value={form.relationship_to_case} onChange={e => setF('relationship_to_case', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white" placeholder="Witness, neighbor, informant..." />
          </div>
        </div>
        <div>
          <Label className="text-slate-300">Observations</Label>
          <Textarea value={form.observations} onChange={e => setF('observations', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={3} />
        </div>
        <div>
          <Label className="text-slate-300">Body Language Notes</Label>
          <Textarea value={form.body_language_notes} onChange={e => setF('body_language_notes', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={3} />
        </div>
        <div>
          <Label className="text-slate-300">Evidence Collected</Label>
          <Textarea value={form.evidence_collected} onChange={e => setF('evidence_collected', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={3} />
        </div>
        <div>
          <Label className="text-slate-300">Summary</Label>
          <Textarea value={form.summary} onChange={e => setF('summary', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={5} />
        </div>
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="interview-action-bar">
        <Button onClick={goBack} variant="outline" className="border-slate-500 !text-slate-200" data-testid="interview-cancel">
          Cancel
        </Button>
        <Button onClick={submit} disabled={saving || !form.interviewee_name?.trim()}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold disabled:opacity-60"
          data-testid="submit-interview-btn">
          {saving
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
            : (isEdit ? <><Save className="w-4 h-4 mr-1.5" /> Save Changes</> : 'Record Interview')}
        </Button>
      </div>
    </div>
  );
}
